// ABOUTME: Make It Right template editor tab for ChildProfilePage
// ABOUTME: Loads and auto-saves Make It Right task template for a child on change

import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MakeItRightTemplateEditor from '../MakeItRightTemplateEditor';
import { makeItRightTemplateAPI } from '../../services/api';
import naluV1Theme from '../../theme/theme.nalu-v1';

interface MakeItRightTabProps {
  childId: number;
}

export default function MakeItRightTab({ childId }: MakeItRightTabProps) {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [tasks, setTasks] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const template = await makeItRightTemplateAPI.getChildTemplate(childId);
      setTasks(template.tasks ?? []);
    } catch (error) {
      console.error('Failed to load Make It Right template:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  async function handleChange(updated: string[]) {
    setTasks(updated);
    if (updated.length === 0 || updated.some((t) => !t.trim())) return;
    await makeItRightTemplateAPI.updateChildTemplate(childId, { tasks: updated });
  }

  async function handleRestoreDefaults() {
    const restored = await makeItRightTemplateAPI.restoreDefaults(childId, tasks);
    setTasks(restored.tasks ?? []);
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      <Typography variant="h6" gutterBottom>Make It Right Template</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These tasks are added to this child&apos;s responsibility list when they receive a square.
      </Typography>
      <MakeItRightTemplateEditor
        tasks={tasks}
        onChange={handleChange}
        onRestoreDefaults={handleRestoreDefaults}
        showRestoreButton
      />
    </>
  );

  // nalu-v1's mockup shows this scaffold editor inside a flat card — Paper picks up the
  // theme's global flat-cream-fill + hairline-border styling automatically (issue #1900).
  return isNaluV1Theme ? (
    <Paper data-testid="make-it-right-editor" sx={{ p: 3 }}>{content}</Paper>
  ) : (
    <Box data-testid="make-it-right-editor">{content}</Box>
  );
}
